import type * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Notice } from '@bufferapp/ui'
import SearchIcon from '@bufferapp/ui/Icon/Icons/Search'
import Text from '@bufferapp/ui/Text'

import {
  Button,
  CloseIcon,
  Flex,
  Form,
  Heading,
  IconButton,
  Input,
  Loader,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { UNSPLASH_SERVICE } from '~publish/legacy/integrations-bar/constants'

import { OrientationDropdownMenu } from './OrientationDropdownMenu'
import { actions, useSearchImagesReducer } from './searchImagesReducer'
import { SearchImagesResults } from './SearchImagesResults'
import { SEARCH_IMAGES_QUERY } from '../../queries'
import { QUERY_RUN, sendIntegrationsEvent } from '../../tracking'
import type { IntegrationsContext, IntegrationsService } from '../../types'

import styles from './SearchImages.module.css'

/**
 *  COMPONENT: SEARCH IMAGES FORM
 */
export const SearchImagesForm = ({
  // todo: rename editor to integrationsContext
  editor,
  service,
}: {
  editor: IntegrationsContext
  service: IntegrationsService
}): JSX.Element | null => {
  const isMounted = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [state, dispatch] = useSearchImagesReducer()
  const [searchImages, { loading }] = useLazyQuery(SEARCH_IMAGES_QUERY)

  useEffect(() => {
    isMounted.current = true
    if (inputRef.current) {
      inputRef.current.focus()
    }
    return (): void => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (state.query && state.query !== '') {
      sendIntegrationsEvent(QUERY_RUN, editor, service.id, state.query)
    }
    searchImages({
      variables: {
        searchTerm: state.query,
        page: state.currentPage,
        orientation: state.orientation,
        source: service?.id,
      },
    })
      .then((result) => {
        if (isMounted.current) {
          const { error, data } = result

          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (error || data?.searchImages?.userFriendlyMessage) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const msg =
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              error?.message || data?.searchImages?.userFriendlyMessage
            dispatch(actions.searchError(msg))
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            dispatch(actions.searchSucccess(data?.searchImages?.images))
          }
        }
        return result
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('error', error)
      })
  }, [
    dispatch,
    searchImages,
    state.currentPage,
    state.query,
    editor,
    service,
    state.orientation,
  ])

  const handleOrientationChange = (newOrientation: string): void => {
    dispatch(actions.changeOrientation(newOrientation))
  }

  if (service === null) return null

  const setSearchTerm = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target
    setInputValue(value)
  }

  const handleClear = (): void => {
    setInputValue('')
    dispatch(actions.cleanQuery({}))
  }

  return (
    <>
      {state.error && (
        <Notice type="alert" disableAnimation className={styles.notification}>
          <Text type="p">{state.error}</Text>
        </Notice>
      )}

      <header className={styles.header}>
        <Heading size="medium" className={styles.heading}>
          {service?.icon}
          {service?.title}
        </Heading>
      </header>
      <section className={styles.dialogBody}>
        <Form name="search-form" className={styles.searchForm}>
          <Form.Field name="search" className={styles.searchField}>
            <VisuallyHidden>
              <Form.Label htmlFor="search">Search</Form.Label>
            </VisuallyHidden>
            <Form.Control>
              <Input
                id="search"
                name="search"
                value={inputValue}
                size="large"
                placeholder={service?.placeholder}
                prefix={<SearchIcon />}
                suffix={
                  inputValue && (
                    <IconButton
                      label="Clear"
                      variant="tertiary"
                      size="small"
                      type="reset"
                      onClick={handleClear}
                    >
                      <CloseIcon />
                    </IconButton>
                  )
                }
                onChange={setSearchTerm}
              />
            </Form.Control>
          </Form.Field>
          <Form.Field name="submit">
            <Form.Control>
              <Button
                type="submit"
                size="large"
                loading={loading}
                onClick={() => {
                  dispatch(actions.search(inputValue))
                }}
              >
                Search
              </Button>
            </Form.Control>
          </Form.Field>
        </Form>
        {service.id === UNSPLASH_SERVICE &&
          state.query &&
          state.query !== '' && (
            <OrientationDropdownMenu
              onChangeOrientation={handleOrientationChange}
            />
          )}
        <SearchImagesResults
          images={state.images}
          getMore={() => dispatch(actions.getMore({}))}
          loading={loading}
          service={service}
          loader={
            <Flex align="center" justify="center" fullWidth fullHeight>
              <Loader />
            </Flex>
          }
        />
      </section>
    </>
  )
}
